<template>
  <div v-if="playlist && Object.keys(playlist).length" :class="[$style.playlist, 'container']">
    <AudioHeader :audio-data="playlist" :class="$style.header"></AudioHeader>
    <div class="space-12-16-above">
      <ol :class="$style.audioTracks">
        <li
          v-for="(track, index) in playlist.tracks"
          :key="index"
          :class="{
            active: currentTrack === index && (isPlaying || !isInitial) && isCurrentPlaylist,
            [$style.first]: index === 0,
          }"
        >
          <AudioPlaylistListItem
            :id="playlist.databaseId"
            :duration="track.duration"
            :index="index"
            :title="track.title"
            @audio-action="(evt) => prepareAudio(evt, index)"
          ></AudioPlaylistListItem>
        </li>
      </ol>
    </div>
    <ProcessContent
      v-if="playlist && playlist.content"
      class="space-12-16-above"
      :content="playlist.content"
      wrapper="section"
    ></ProcessContent>
    <AudioTags
      v-if="playlist && playlist.tags.nodes.length"
      :tags="playlist.tags.nodes"
    ></AudioTags>
  </div>
</template>

<script setup lang="ts">
import { AudioData } from '@/types/audio.interface';
import AudioHeader from '@/components/audio/AudioHeader.vue';
import AudioPlaylistListItem from '@/components/audio/AudioPlaylistListItem.vue';
import { audioPlaylistQuery } from '@/queries/GetAudioPlaylist';
import { AudioState } from '@/types/audio.store.interface';
import { computed, defineAsyncComponent, ref } from 'vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { storeToRefs } from 'pinia';
import { useAudioStore } from '@/stores/audioStore';
import { useQuery } from 'villus';

const AudioTags = defineAsyncComponent(() => import('@/components/audio/AudioTags.vue'));

const props = defineProps<{
  slug: string;
}>();

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const store = useAudioStore();
const { audioId, currentTrack, isInitial, isPlaying } = storeToRefs(store);
const { updateAudioState } = store;

const playlist = ref<AudioData>();

const isCurrentPlaylist = computed(() => playlist.value?.databaseId === audioId?.value);

const { data } = useQuery({
  onData: data => {
    if (data.audioPlaylist && Object.keys(data.audioPlaylist).length) {
      playlist.value = data.audioPlaylist;
      setMetaTitle(data.audioPlaylist.title);
      setMetaDescription(data.audioPlaylist.excerpt);
      dataLoaded();
    }

    if (!data.audioPlaylist || Object.keys(data.audioPlaylist).length === 0) {
      handleError('AudioPlaylist', props.slug);
    }
  },
  query: audioPlaylistQuery,
  variables: { slug: props.slug },
});

function prepareAudio(event: PointerEvent | MouseEvent, index: number): void {
  updateAudioState({ isInitial: false, lastFocused: event.target });
  const audioData: AudioState = {};

  if (audioId && audioId.value !== playlist.value?.databaseId) {
    audioData.playlistTitle = playlist.value?.title;
    audioData.tracks = playlist.value?.tracks;
    audioData.isPlaying = true;
    audioData.currentTrack = index ? index : 0;
    audioData.audioId = playlist.value?.databaseId;
  } else if (currentTrack && index !== currentTrack.value) {
    audioData.isPlaying = true;
    audioData.currentTrack = index;
  } else {
    if (isPlaying) {
      audioData.isPlaying = !isPlaying.value;
    }
  }

  audioData.source = 'permalink';
  updateAudioState(audioData);
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: audioPlaylistQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
.audio-tracks {
  @include audio-playlist;
}

@media (min-width: $break-point) {
  .playlist {
    @include grid;

    > * {
      grid-column: 1 / span 8;
    }

    .discover-more {
      grid-column: 1 / span 8;
    }

    .header {
      grid-column: span 12;
    }
  }
}
</style>
